<template>
  <div class="library-list-view">
    <page-header :breadcrumbs="libraryBreadcrumbs" />
    <loading-bar v-if="loading" />
    <div
      v-else
      class="library-list-content"
    >
      <div class="library-list-header">
        <div class="asset-type-tabs">
          <button
            v-for="assetType in tabs"
            :key="assetType"
            class="asset-type-tab-button"
            :class="{ active: activeAssetType === assetType }"
            @click="setActiveAssetType(assetType)"
          >
            {{ assetType }} ({{ counts[assetType] }})
          </button>
        </div>
        <div class="asset-type-selection">
          <listbox
            label=""
            :options="tabs.map(assetType => ({ text: `${assetType} (${ counts[assetType] })`, value: assetType }))"
            :value="activeAssetType"
            @change="setActiveAssetType"
          />
        </div>
        <div class="sort">
          <span class="label">Sort By</span>
          <listbox
            label="Sort by"
            :options="sortOptions"
            :value="sortOrder"
            @change="setSortOrder"
          />
        </div>
      </div>
      <div class="list-container asset-list">
        <div class="list-heading row asset-type-header">
          <icon
            class="asset-type-icon"
            :name="headerIcon"
          />
          {{ activeAssetType }}
        </div>
        <!-- this is kind of super janky but it's a patch while the signatures of the two components are different... -->
        <component
          :is="asset.listRowType"
          v-for="asset in displayedAssets"
          :key="`${asset.assetType}-${asset.id}`"
          :attachment="asset"
          :deck="asset"
          :show-lock="false"
          :source="bookmarkSource"
          :topic="asset"
          @attachment-clicked="handleRowClick(asset)"
          @row-clicked="handleRowClick(asset)"
        />
      </div>
      <div
        v-if="showPagination"
        class="library-list-pagination"
      >
        <button
          class="pagination-button"
          :class="{ hidden: !showPrevButton }"
          @click="prevPage"
        >
          <icon
            name="mastery-arrow-left"
            variant="white"
          />
        </button>
        <span class="pagination-text">{{ currentPage }} of {{ totalPages }}</span>
        <button
          class="pagination-button"
          :class="{ hidden: !showNextButton }"
          @click="nextPage"
        >
          <icon
            name="mastery-arrow-right"
            variant="white"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AttachmentListRow from '@/components/attachments/listRow';
import DeckListRow from '@/components/decks/listRow';
import Icon from '@/components/singletons/icon';
import Listbox from '@/components/singletons/listbox';
import LoadingBar from '@/components/singletons/loading-bar.vue';
import PageHeader from '@/components/singletons/pageHeader';
import TopicListRow from '@/components/topics/listRow';

import GlobalMixin from '@/mixins/global';
import LibraryMixin from '@/mixins/library';

import { titleFromAsset } from '@/utils';

const ASSET_TYPES = {
  ALL: 'All',
  ARTICLES: 'Articles',
  AUDIO: 'Audio',
  HUMANKIT: '3D Anatomy',
  IMAGES: 'Images',
  SERIES: 'Series',
  VIDEOS: 'Video',
};

const PAGINATION_OFFSET = 25;

export default {
  name: 'LibraryList',
  components: { AttachmentListRow, DeckListRow, Icon, Listbox, LoadingBar, PageHeader, TopicListRow },
  mixins: [GlobalMixin, LibraryMixin],
  data() {
    return {
      activeAssetType: ASSET_TYPES.ALL,
      assets: null,
      loading: true,
      offset: 0,
      sortOrder: 'alphabetical',
      tabs: [
        ASSET_TYPES.ALL,
        ASSET_TYPES.ARTICLES,
        ASSET_TYPES.AUDIO,
        ASSET_TYPES.HUMANKIT,
        ASSET_TYPES.IMAGES,
        ASSET_TYPES.SERIES,
        ASSET_TYPES.VIDEOS,
      ],
    };
  },
  computed: {
    attachments() {
      return (this.assets?.attachments || []).map(a => this.$store.getters['attachments/get'][a]).filter(a => a);
    },
    bookmarkSource() {
      return {
        type: this.currentResource?.name || 'Library',
        id: this.currentResource?.id,
      };
    },
    counts() {
      const counts = {
        [ASSET_TYPES.ARTICLES]: this[ASSET_TYPES.ARTICLES].length,
        [ASSET_TYPES.AUDIO]: this[ASSET_TYPES.AUDIO].length,
        [ASSET_TYPES.HUMANKIT]: this[ASSET_TYPES.HUMANKIT].length,
        [ASSET_TYPES.IMAGES]: this[ASSET_TYPES.IMAGES].length,
        [ASSET_TYPES.SERIES]: this[ASSET_TYPES.SERIES].length,
        [ASSET_TYPES.VIDEOS]: this[ASSET_TYPES.VIDEOS].length,
      };
      counts[ASSET_TYPES.ALL] = Object.keys(counts).reduce((acc, curr) => acc += counts[curr], 0);

      return counts;
    },
    currentPage() {
      return Math.floor(this.offset / PAGINATION_OFFSET) + 1;
    },
    decks() {
      return (this.assets?.decks || []).map(d => this.$store.getters['decks/get'][d]);

    },
    displayedAssets() {
      return this[this.activeAssetType].slice(this.offset, this.offset + PAGINATION_OFFSET);
    },
    headerIcon() {
      if (this.activeAssetType === ASSET_TYPES.ARTICLES) {
        return 'topics';
      } else if (this.activeAssetType === ASSET_TYPES.VIDEOS) {
        return 'video-camera';
      } else if (this.activeAssetType === ASSET_TYPES.AUDIO) {
        return 'speaker';
      } else if (this.activeAssetType === ASSET_TYPES.SERIES) {
        return 'series';
      }
    },
    showNextButton() {
      return this.showPagination && ((this.offset + PAGINATION_OFFSET) < this[this.activeAssetType].length);
    },
    showPagination() {
      return this[this.activeAssetType].length > PAGINATION_OFFSET;
    },
    showPrevButton() {
      return this.showPagination && this.offset > 0;
    },
    sortOptions() {
      return [{ text: 'A - Z', value: 'alphabetical' },{ text: 'Z - A', value: 'reverse-alphabetical' }];
    },
    topics() {
      return (this.assets?.topics || []).map(t => this.$store.getters['topics/topics'][t]);
    },
    totalPages() {
      return Math.floor(this.counts[this.activeAssetType] / PAGINATION_OFFSET) + 1;
    },
    [ASSET_TYPES.ALL]() {
      const assets = [...this.attachments, ...this.decks, ...this.topics];
      return this.sortList(assets);
    },
    [ASSET_TYPES.ARTICLES]() {
      return this.sortList(this.topics);
    },
    [ASSET_TYPES.AUDIO]() {
      return this.sortList(this.attachments.filter(a => a.mediaType === 'audio'));
    },
    [ASSET_TYPES.HUMANKIT]() {
      return this.sortList(this.attachments.filter(a => a.mediaType === 'biodigital'));
    },
    [ASSET_TYPES.IMAGES]() {
      return this.sortList(this.attachments.filter(a => a.mediaType === 'image'));
    },
    [ASSET_TYPES.SERIES]() {
      return this.sortList(this.decks);
    },
    [ASSET_TYPES.VIDEOS]() {
      return this.sortList(this.attachments.filter(a => a.mediaType === 'video'));
    },
  },
  created() {
    this.$store.dispatch('flashcardStates/fetchStates', ['attachments', 'topics']);
  },
  mounted() {
    if (!this.tags) {
      this.$router.push({ name: 'library#index' });
    } else {
      this.fetchAssets();
      this.$watch('tags', this.resetView);
    }
  },
  methods: {
    fetchAssets() {
      this.loading = true;
      const params = { tags: this.tags.map(t => t.id) };
      this.$store.getters['session/ajax'].get('/library', { params }).then(response => {
        const { attachments, decks, topics, records } = response.data;
        this.assets = records;
        this.$store.commit('attachments/setAttachments', attachments);
        this.$store.commit('decks/set', decks);
        this.$store.commit('topics/setTopics', topics);
        this.loading = false;
      });
    },
    handleRowClick(asset) {
      const analyticsParams = {
        assetId: asset.id,
        assetType: asset.assetType,
        assetTitle: titleFromAsset(asset, asset.assetType),
        assetLocation: 'View All',
        childTag: this.tags[this.tags.length - 1]?.name,
        parentTag: this.tags[this.tags.length - 2]?.name,
      };
      this.$analytics.events.viewedLibraryAsset(analyticsParams);

      const query = {
        tag: this.$route.query?.tag,
      };
      if (this.currentQuery?.nav) {
        query.nav = this.currentQuery.nav;
      }

      this.$router.push({
        ...asset.libraryRoute,
        query,
      });
    },
    nextPage() {
      if (this.offset + PAGINATION_OFFSET <= this[this.activeAssetType].length) {
        this.offset = this.offset + PAGINATION_OFFSET;
      }
    },
    prevPage() {
      if (this.offset > 0) {
        this.offset = this.offset - PAGINATION_OFFSET;
      }
    },
    resetView() {
      this.assets = null;
      this.offset = 0;
      this.fetchAssets();
    },
    setActiveAssetType(assetType) {
      this.offset = 0;
      this.activeAssetType = assetType;
    },
    setSortOrder(sortOrder) {
      this.$analytics.events.sortedLibrary(sortOrder === 'alphabetical' ? 'Asc' : 'Desc');
      this.offset = 0;
      this.sortOrder = sortOrder;
    },
    sortList(items) {
      return items.sort((a, b) => {
        if (this.sortOrder === 'alphabetical') {
          return a.assetTitle.localeCompare(b.assetTitle);
        } else {
          return b.assetTitle.localeCompare(a.assetTitle);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.library-list-view {
  background: #EDF1F5;
  @include full-padding;

  @include breakpoint(small) {
    padding: 1rem !important;
  }

  .library-list-content {
    .library-list-header {
      align-items: center;
      display: flex;
      justify-content: space-between;

      .asset-type-tabs {
        .asset-type-tab-button {
          background: transparent;
          border-bottom: 1px solid #333333;
          color: #333333;
          font-size: rem-calc(12);
          margin-left: rem-calc(24);
          padding: rem-calc(4);

          &.active {
            border-color: #00dbfb;
            color: #000000;
            font-weight: $bold;
          }

          &:first-child {
            margin-left: 0;
          }
        }
      }

      .asset-type-selection {
        display: none;
      }

      .asset-type-selection,
      .sort {
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        z-index: 100;

        .label {
          color: #767676;
          margin-right: rem-calc(8);
        }
      }

      @include breakpoint(small) {
        align-items: flex-end;

        .asset-type-tabs {
          @include breakpoint(small) {
            display: none;
          }
        }

        .asset-type-selection {
          @include breakpoint(small) {
            display: initial;
          }
        }

        .sort {
          flex: 0;
        }
      }
    }

    .asset-list {
      margin: rem-calc(32) 0;

      .asset-type-header {
        color: #000000;
        text-transform: uppercase;

        .asset-type-icon {
          height: rem-calc(16);
          margin: 0 rem-calc(12) 0 rem-calc(4);
          width: rem-calc(16);
        }
      }
    }

    .library-list-pagination {
      text-align: center;

      .pagination-button {
        align-items: center;
        background-color: #000000;
        border-radius: 100%;
        display: inline-flex;
        height: rem-calc(24);
        justify-content: center;
        width: rem-calc(24);

        &.hidden {
          visibility: hidden;
        }
      }

      .pagination-text {
        display: inline-block;
        width: rem-calc(100);
      }
    }
  }
}
</style>
